const REQUESTED_DATE_TIME_ZONE = 'Europe/Rome';
const REQUESTED_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: REQUESTED_DATE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function normalizeQuantityToStep(value, { min = 1, max = null, step = 1, preserveZero = false } = {}) {
  const minimum = Number.isFinite(Number(min)) ? Math.max(0, Math.trunc(Number(min))) : 0;
  const increment = Number.isFinite(Number(step)) && Number(step) > 0 ? Math.trunc(Number(step)) : 1;
  const maximumValue = max === null || max === '' ? null : Number(max);
  const maximum = Number.isFinite(maximumValue) ? Math.trunc(maximumValue) : null;

  if (maximum !== null && maximum < minimum) return null;

  const quantityValue = value === '' || value === null ? Number.NaN : Number(value);
  if (preserveZero && minimum === 0 && Number.isFinite(quantityValue) && quantityValue <= 0) return 0;

  const requested = Number.isFinite(quantityValue) ? Math.trunc(quantityValue) : minimum;
  const maximumAligned = maximum === null
    ? Infinity
    : minimum + Math.floor((maximum - minimum) / increment) * increment;
  if (maximumAligned < minimum) return null;

  const steps = Math.max(0, (requested - minimum) / increment);
  const rounded = minimum + Math.floor(steps + 0.5) * increment;
  return Math.min(maximumAligned, Math.max(minimum, rounded));
}

export function getDefaultRequestedDate(now = new Date()) {
  const parts = Object.fromEntries(REQUESTED_DATE_FORMATTER.formatToParts(now).map((part) => [part.type, part.value]));
  const date = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + 3, 12));
  if (date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() + 2);
  else if (date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);

  return [date.getUTCFullYear(), String(date.getUTCMonth() + 1).padStart(2, '0'), String(date.getUTCDate()).padStart(2, '0')].join('-');
}

function normalizeCustomQuantity(input) {
  const selector = input.closest('quantity-selector-component');
  if (!selector?.classList.contains('tg-quantity-selector') || input.disabled) return;

  const values = selector.getCurrentValues?.();
  const minimum = values?.min ?? (Number(input.min) || 0);
  const step = values?.step ?? (Number(input.step) || 1);
  const maximum = typeof selector.getEffectiveMax === 'function'
    ? selector.getEffectiveMax()
    : input.max === ''
      ? null
      : selector.localName === 'cart-quantity-selector-component'
        ? Number(input.max)
        : Math.max(Number(input.min) || 0, Number(input.max) - (Number(input.dataset.cartQuantity) || 0));
  const quantity = normalizeQuantityToStep(input.value, {
    min: minimum,
    max: maximum,
    step,
    preserveZero: minimum === 0,
  });

  if (quantity !== null) input.value = String(quantity);
}

function normalizeCustomQuantities(root) {
  root.querySelectorAll('input[type="number"][ref="quantityInput"]').forEach(normalizeCustomQuantity);
}

function refreshRequestedDateDefault(target) {
  const requestedDate = getDefaultRequestedDate();
  if (target instanceof HTMLInputElement) target.value = requestedDate;
  else target.dataset.requestedDate = requestedDate;
}

function refreshRequestedDateDefaults(root) {
  if (root.matches?.('[data-tg-requested-date-default]')) refreshRequestedDateDefault(root);
  root.querySelectorAll?.('[data-tg-requested-date-default]').forEach(refreshRequestedDateDefault);
}

if (typeof window !== 'undefined') {
  window.addEventListener('blur', (event) => {
    if (event.target instanceof HTMLInputElement) normalizeCustomQuantity(event.target);
  }, true);

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
      normalizeCustomQuantity(event.target);
    }
  }, true);

  window.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement) {
      normalizeCustomQuantities(event.target);
      refreshRequestedDateDefaults(event.target);
    }
  }, true);

  window.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[data-tg-requested-date-default]');
    if (target) refreshRequestedDateDefault(target);
  }, true);

  window.addEventListener('quantity-selector:update', (event) => {
    if (!(event.target instanceof Element)) return;
    const quickOrder = event.target.closest('quick-order-list-component[data-tg-requested-date-default]');
    if (quickOrder) refreshRequestedDateDefault(quickOrder);
  }, true);
}